import { Fragment, type ReactNode, useEffect, useId, useMemo, useRef, useState } from "react";
import { toast } from "sonner";

import {
  useAddEngagement,
  useDeactivateInOkta,
  useDeleteEngagement,
  useExportIdentityToOkta,
  useIdentity,
  useIdentityAudit,
  useIdentityOktaRequests,
  useLookups,
  useOktaSync,
  usePartnerDris,
  useRestoreSystemAccess,
  useReviveEngagement,
  useSetEngagementEndDate,
  useSetEngagementStartDate,
  useSetEngagementTerminationDate,
  useSetEngagementBeamDetail,
  useTerminateEngagement,
  useUpdateEngagement,
  useUpdateIdentity
} from "src/api/hooks";
import { AuditEventList } from "src/components/audit-rows";
import { DepartmentField } from "src/components/department-field";
import { PartnerDRIField } from "src/components/partner-dri-field";
import { ReadFailure } from "src/components/read-failure";
import {
  Badge,
  Button,
  Card,
  Combobox,
  ConfirmDialog,
  Empty,
  Field,
  Input,
  RadioGroup,
  Select,
  Table,
  cn,
  humanPopulation,
  humanStatus,
  statusTone
} from "src/components/ui";
import type {
  BeamType,
  EngagementAttributesInput,
  Gender,
  IdentityFieldsInput,
  NameChangeInput
} from "src/graphql/generated";
import { buildAuditNames, groupAuditRows } from "src/lib/audit";
import { describeBeamDetail, isBeamSubcategory } from "src/lib/beam";
import { FORMER_NAME_TYPES, describeFormerName, outgoingLegalNames } from "src/lib/former-names";
import { businessDate } from "src/lib/dates";
import { describeOktaRequest, describeOktaSync, formatSyncTime } from "src/lib/okta-sync";
import {
  assignableOrganizations,
  describeEngagementFirm,
  organizationsForEditing
} from "src/lib/partner-orgs";
import { BeamFields } from "src/screens/create-identity";
import {
  DeactivateInOktaDialog,
  mayDeactivateInOkta,
  systemAccessLines,
  systemAccessNeverGranted
} from "src/screens/system-access";
import { canWrite, type Viewer } from "src/screens/types";

export function IdentityDetail({
  id,
  viewer,
  onBack
}: {
  id: string;
  viewer: Viewer;
  onBack: () => void;
}) {
  const { data, isPending, error } = useIdentity(id);
  const identity = data?.identity;

  if (isPending) {
    return <Empty>Loading…</Empty>;
  }
  if (error) {
    return <ReadFailure error={error} />;
  }
  if (!identity) {
    // Absent and out-of-scope are the same answer from the backend on purpose, so
    // this message must not imply the person exists.
    return (
      <Card title="Not found">
        <p className="text-sm text-slate-600">No such person, or not one you can see.</p>
        <Button className="mt-3" variant="secondary" onClick={onBack}>
          Back
        </Button>
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        {/* h2, not h1: the console's masthead already carries the page's one h1 on
            every view, and a screen reader announcing two top-level headings gives
            the page two titles. h2 is also where this belongs in the ladder the rest
            of the app uses — Card titles are h2 and their subsections h3 — so the
            outline gains a level rather than skipping one. Size is unchanged. */}
        <h2 className="text-lg font-semibold text-slate-900">
          {identity.legalFullName || identity.preferredFullName || "Unnamed"}
        </h2>
        <div className="flex items-center gap-2">
          <ExportToOkta identity={identity} viewer={viewer} />
          <SystemAccess identity={identity} viewer={viewer} />
          <Button variant="secondary" onClick={onBack}>
            Back to list
          </Button>
        </div>
      </div>
      <SystemAccessStatus identity={identity} />
      <OktaSyncStatus identityId={identity.id} viewer={viewer} />

      <IdentityForm identity={identity} viewer={viewer} />
      <Engagements identity={identity} viewer={viewer} />
      <AuditHistory
        identityId={id}
        canReadPartners={viewer.readablePopulations.includes("PARTNER")}
      />
      <OktaHistory identityId={id} viewer={viewer} />
    </div>
  );
}

type Identity = NonNullable<ReturnType<typeof useIdentity>["data"]>["identity"];

/**
 * Whether Okta holds the latest of this person, from what this service sent and
 * what Okta answered. Shown to writers only, for the reason the People list's
 * column is.
 */
function OktaSyncStatus({ identityId, viewer }: { identityId: string; viewer: Viewer }) {
  const shown = viewer.oktaExportEnabled && viewer.writablePopulations.length > 0;
  const { data, error } = useOktaSync([identityId], shown);
  if (!shown) {
    return null;
  }
  if (error) {
    return (
      <p className="text-xs text-amber-800">Okta sync status could not be read: {error.message}</p>
    );
  }
  const sync = data?.oktaSync[0];
  if (!sync) {
    return null;
  }
  const { label, tone, caption, detail } = describeOktaSync(sync);
  const last = sync.lastRequest;
  // In step, the caption already says when Okta last accepted them.
  const facts = [
    sync.changedAt ? `Last changed ${formatSyncTime(sync.changedAt)}` : null,
    sync.state !== "IN_SYNC" && sync.syncedAt
      ? `Last accepted ${formatSyncTime(sync.syncedAt)}`
      : null,
    last?.oktaRequestId ? `Okta request ${last.oktaRequestId}` : null
  ].filter(Boolean);
  return (
    <section aria-label="Okta sync" className="flex flex-col gap-1 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-slate-600">Okta sync</span>
        {tone ? (
          <Badge tone={tone}>{label}</Badge>
        ) : (
          <span className="text-slate-700">{label}</span>
        )}
        {caption ? <span className="text-xs text-slate-500">{caption}</span> : null}
      </div>
      {detail ? <p className="text-xs text-red-700">{detail}</p> : null}
      {facts.length > 0 ? <p className="text-xs text-slate-500">{facts.join(" · ")}</p> : null}
    </section>
  );
}

/**
 * Whether the person has system access, for everyone who can see them: it is a fact
 * about the person, not about Okta's copy of them, which the sync line below is.
 */
function SystemAccessStatus({ identity }: { identity: NonNullable<Identity> }) {
  return (
    <section aria-label="System access" className="flex flex-wrap items-center gap-2 text-sm">
      <span className="text-slate-600">System access</span>
      {identity.hasSystemAccess ? (
        <Badge tone="green">Granted</Badge>
      ) : (
        <Badge tone="slate">Not granted</Badge>
      )}
      {systemAccessLines(identity).map((line) => (
        <span key={line} className="text-xs text-slate-500">
          {line}
        </span>
      ))}
    </section>
  );
}

/**
 * Sends this one person to Okta, confirmed first because it writes to another
 * system. Offered on the rule the server enforces: Okta export is configured, the
 * person has system access, and the viewer may write a population this person holds
 * a current term in. Someone without access is not offered it — only granting or
 * restoring it puts them in Okta.
 */
function ExportToOkta({ identity, viewer }: { identity: NonNullable<Identity>; viewer: Viewer }) {
  const exportToOkta = useExportIdentityToOkta();
  const [confirming, setConfirming] = useState(false);

  const offered =
    viewer.oktaExportEnabled &&
    identity.hasSystemAccess &&
    identity.engagements.some((e) => e.isCurrent && canWrite(viewer, e.population));
  if (!offered) {
    return null;
  }
  const name = identity.preferredFullName || identity.legalFullName || "this person";

  return (
    <>
      <Button variant="secondary" onClick={() => setConfirming(true)}>
        Export to Okta
      </Button>
      <ConfirmDialog
        open={confirming}
        title="Export to Okta?"
        confirmLabel="Export to Okta"
        tone="primary"
        busy={exportToOkta.isPending}
        onCancel={() => setConfirming(false)}
        onConfirm={() =>
          exportToOkta.mutate(identity.id, { onSuccess: () => setConfirming(false) })
        }
      >
        <p>
          This sends <strong>{name}</strong> to the Okta identity source. Okta creates them, or
          updates them if they are already there.
        </p>
        <p className="mt-2 text-xs text-slate-500">
          {identity.workEmail
            ? `Their Okta username is ${identity.workEmail}. `
            : "They have no work email, so one is saved for them first and becomes their Okta username: first.last@need.ai, or first.last1@need.ai and up if that is taken. "}
          Ending their last current engagement, or Remove system access, takes them out of Okta
          again.
        </p>
      </ConfirmDialog>
    </>
  );
}

/**
 * Remove system access, or grant or restore it, beside Export to Okta: each decides
 * whether Okta holds this person, and none touches the contract. Each takes effect in
 * the request, with Okta told before anything is saved. Both belong to the person:
 * removing takes them out of Okta whichever terms keep them there, so it needs the
 * permission that ends every current term. Once the person exists, granting or
 * restoring is the only way access is given.
 */
function SystemAccess({ identity, viewer }: { identity: NonNullable<Identity>; viewer: Viewer }) {
  const deactivateInOkta = useDeactivateInOkta();
  const restore = useRestoreSystemAccess();
  const [deactivating, setDeactivating] = useState(false);
  const [restoring, setRestoring] = useState(false);

  const current = identity.engagements.filter((e) => e.isCurrent);
  const mayEndEvery = current.length > 0 && current.every((e) => canWrite(viewer, e.population));
  const name = identity.legalFullName || "this person";
  const mayRestore =
    !identity.hasSystemAccess && current.some((e) => canWrite(viewer, e.population));
  const granting = systemAccessNeverGranted(identity);
  const giveLabel = granting ? "Grant system access" : "Restore system access";

  return (
    <>
      {mayEndEvery && mayDeactivateInOkta(identity, current) ? (
        <Button
          variant="danger"
          disabled={deactivateInOkta.isPending}
          onClick={() => setDeactivating(true)}
        >
          Remove system access
        </Button>
      ) : null}
      {mayRestore ? (
        <Button variant="secondary" disabled={restore.isPending} onClick={() => setRestoring(true)}>
          {giveLabel}
        </Button>
      ) : null}

      <ConfirmDialog
        open={restoring}
        title={`${giveLabel}?`}
        confirmLabel={giveLabel}
        tone="primary"
        busy={restore.isPending}
        onCancel={() => setRestoring(false)}
        onConfirm={() =>
          restore.mutate(
            { identityId: identity.id, granting },
            { onSuccess: () => setRestoring(false) }
          )
        }
      >
        {granting ? (
          <>
            <p>
              This gives <strong>{name}</strong> system access
              {viewer.oktaExportEnabled ? " and exports them to Okta now" : ""}. Nothing else does:
              adding or reviving an engagement leaves them without it.
            </p>
            <p className="mt-2 text-xs text-slate-500">
              {viewer.oktaExportEnabled
                ? "If Okta does not accept the export, nothing is saved and they stay without access. "
                : ""}
              They keep access until their engagements end or it is removed. Every step is recorded
              in the history below.
            </p>
          </>
        ) : (
          <>
            <p>
              This gives <strong>{name}</strong> their system access back
              {viewer.oktaExportEnabled ? " and exports them to Okta now" : ""}. Nothing else does:
              adding or reviving an engagement leaves it removed.
            </p>
            <p className="mt-2 text-xs text-slate-500">
              {viewer.oktaExportEnabled
                ? "If Okta does not accept the export, nothing is saved and their access stays removed. "
                : ""}
              Their removal is cleared with it, so they keep access until their engagements end.
              Every step is recorded in the history below.
            </p>
          </>
        )}
      </ConfirmDialog>

      <DeactivateInOktaDialog
        open={deactivating}
        personName={name}
        busy={deactivateInOkta.isPending}
        onCancel={() => setDeactivating(false)}
        onConfirm={() =>
          deactivateInOkta.mutate(identity.id, { onSuccess: () => setDeactivating(false) })
        }
      />
    </>
  );
}

function IdentityForm({ identity, viewer }: { identity: NonNullable<Identity>; viewer: Viewer }) {
  const update = useUpdateIdentity();

  // Identity fields are shared across populations, so a writer in any population
  // *this person belongs to* may edit them — the same intersection the backend
  // takes. The engagements here arrive already scoped to what the caller may read,
  // and a writer always reads what it writes, so intersecting against them lands on
  // the backend's answer.
  //
  // The earlier version checked "writes anywhere", contradicting its own comment: a
  // partner-writer who also holds odc-reader saw Save controls on an ODC-only
  // person, and the mutation then refused them. The lifecycle buttons below already
  // did this per engagement, which is what made the inconsistency obvious.
  const editable = identity.engagements.some((e) => canWrite(viewer, e.population));

  const [fields, setFields] = useState<IdentityFieldsInput>({});
  const value = (key: keyof IdentityFieldsInput) =>
    fields[key] ?? (identity[key as keyof typeof identity] as string | null) ?? "";
  const set =
    (key: keyof IdentityFieldsInput) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
      setFields((prev) => ({ ...prev, [key]: e.target.value }));

  // gender is the one field that cannot use the empty-string-clears convention: it
  // is a GraphQL enum, and "" is not a member. Blank sends an explicit null, which
  // the API distinguishes from an omitted field and treats as a clear.
  const setGender = (e: React.ChangeEvent<HTMLSelectElement>) =>
    setFields((prev) => ({ ...prev, gender: (e.target.value as Gender) || null }));
  const genderValue = fields.gender !== undefined ? (fields.gender ?? "") : (identity.gender ?? "");

  // `workLocation` needs no such special case: "" is a legal value for it and clears
  // the column, which is exactly what `value` already returns for a cleared field.
  const { data: lookups } = useLookups();
  const countryOptions = useMemo(
    () => (lookups?.countries ?? []).map((c) => ({ value: c.code, label: c.name, hint: c.code })),
    [lookups]
  );

  // Read through `value` rather than off the loaded row, so clearing the only address
  // says so while the box is still empty. A stored identity always has one — the
  // identity_requires_an_email CHECK — so an unsaved edit is the only way here, and
  // the save the edit is heading for is the one the constraint would refuse.
  const emailMissing = !value("workEmail").trim() && !value("personalEmail").trim();

  // What this edit would replace, and therefore whether the two questions below get
  // asked. PRIOR_LEGAL by default: this is the moment a legal name is being watched
  // change, so it is the safe answer where nothing more specific applies — the
  // opposite of INFRA-594's refusal to backfill the kind onto old rows, where nobody
  // had watched anything.
  const [nameChange, setNameChange] = useState<NameChangeInput>({
    nameType: "PRIOR_LEGAL",
    heldUntil: ""
  });
  const outgoing = outgoingLegalNames(identity, {
    legalFirstName: value("legalFirstName"),
    legalMiddleName: value("legalMiddleName"),
    legalLastName: value("legalLastName"),
    legalFullNameLocal: value("legalFullNameLocal")
  });
  // Required, not offered. A name change with no record of what it was is the state
  // this form exists to stop producing, and the API refuses the save regardless —
  // blocking it here only means the refusal does not arrive as a toast.
  const nameChangeIncomplete = outgoing.length > 0 && !nameChange.heldUntil;

  return (
    <Card
      title="Identity"
      actions={
        editable ? (
          <Button
            disabled={update.isPending || Object.keys(fields).length === 0 || nameChangeIncomplete}
            onClick={() =>
              update.mutate(
                {
                  id: identity.id,
                  fields,
                  // Sent only when something is actually being replaced: the API
                  // refuses a reason with nothing to explain rather than ignoring it.
                  nameChange: outgoing.length > 0 ? nameChange : undefined
                },
                {
                  onSuccess: () => {
                    setFields({});
                    setNameChange({ nameType: "PRIOR_LEGAL", heldUntil: "" });
                  }
                }
              )
            }
          >
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        ) : null
      }
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Field label="Legal first name">
          <Input
            disabled={!editable}
            value={value("legalFirstName")}
            onChange={set("legalFirstName")}
          />
        </Field>
        <Field label="Legal middle name">
          <Input
            disabled={!editable}
            value={value("legalMiddleName")}
            onChange={set("legalMiddleName")}
          />
        </Field>
        <Field label="Legal last name">
          <Input
            disabled={!editable}
            value={value("legalLastName")}
            onChange={set("legalLastName")}
          />
        </Field>
        <Field label="Local legal name" hint="As written on the legal document">
          <Input
            disabled={!editable}
            value={value("legalFullNameLocal")}
            onChange={set("legalFullNameLocal")}
          />
        </Field>
        <Field label="Preferred first name">
          <Input
            disabled={!editable}
            value={value("preferredFirstName")}
            onChange={set("preferredFirstName")}
          />
        </Field>
        <Field label="Preferred last name">
          <Input
            disabled={!editable}
            value={value("preferredLastName")}
            onChange={set("preferredLastName")}
          />
        </Field>
        {/* The one-email rule belongs to the pair, so it is stated once, beneath both
            boxes, and only while it is unmet. It used to sit permanently under the
            personal email alone, where it read as a rule about that box and was
            asserted even with both addresses filled. Two columns wide so the boxes
            keep their cells and the message lands under both. */}
        <div className="flex flex-col gap-1.5 md:col-span-2">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label="Work email">
              <Input disabled={!editable} value={value("workEmail")} onChange={set("workEmail")} />
            </Field>
            <Field label="Personal email">
              <Input
                disabled={!editable}
                value={value("personalEmail")}
                onChange={set("personalEmail")}
              />
            </Field>
          </div>
          {emailMissing ? (
            <p className="text-xs text-slate-500">
              Enter at least one email address — work or personal.
            </p>
          ) : null}
        </div>
        <Field label="Work country" hint="Where the person works, not where they live">
          {/* A picker rather than a text box: the field holds an ISO 3166-1 code, and
              the options come from the backend's list so the console cannot offer a
              value the save would refuse. */}
          <Combobox
            label="Work country"
            disabled={!editable}
            value={value("workLocation")}
            options={countryOptions}
            onChange={(code) => setFields((prev) => ({ ...prev, workLocation: code }))}
            emptyLabel="Not recorded"
            placeholder="Search countries…"
          />
        </Field>
        <Field label="Gender">
          <Select disabled={!editable} value={genderValue} onChange={setGender}>
            <option value="">Not recorded</option>
            <option value="FEMALE">Female</option>
            <option value="MALE">Male</option>
          </Select>
        </Field>
      </div>

      {outgoing.length > 0 ? (
        // Two questions, asked at the one moment the answers are in front of a human.
        // A standalone "manage former names" screen is a screen nobody visits, and the
        // field stays empty; a third question here is how a form gets abandoned.
        //
        // The name itself is not among them. It is the outgoing value taken from the
        // record, shown rather than typed, which is why there is no editor for
        // correcting a former name afterwards and no need for one.
        <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-3">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-amber-900">
            Recording the name this replaces
          </h3>
          {/* Joined rather than one node each: replacing both the composed name and
              the local one records two former names, and the two strings can be
              identical, which a keyed list has no honest key for. */}
          <p className="mt-1 text-sm font-medium text-amber-900">{outgoing.join(" · ")}</p>
          <div className="mt-2 grid grid-cols-1 gap-3 md:grid-cols-2">
            {/* All five kinds visible at once, with what each means, because that is
                the choice a writer cannot make from the labels alone — maiden is a kind
                of prior legal name and romanization is a kind of transliteration, and a
                closed dropdown shows one and hides the other four. Spans both columns:
                stacked hints are taller than the date field beside them.

                A fixed radio `name` is safe here where `admin-users` needs a per-row
                one — this panel renders once, on the identity being edited. */}
            <div className="md:col-span-2">
              <RadioGroup
                name="name-change-kind"
                legend="Kind of change"
                value={nameChange.nameType}
                options={FORMER_NAME_TYPES}
                onChange={(nameType) => setNameChange((prev) => ({ ...prev, nameType }))}
              />
            </div>
            <Field
              label="Last day of the previous name"
              hint="Inclusive. For a marriage, the day before the new name took effect"
            >
              <Input
                type="date"
                aria-label="Last day of the previous name"
                value={nameChange.heldUntil}
                onChange={(e) => setNameChange((prev) => ({ ...prev, heldUntil: e.target.value }))}
              />
            </Field>
          </div>
          {nameChangeIncomplete ? (
            <p className="mt-2 text-xs text-amber-900">
              A legal name cannot be changed without saying what the change was.
            </p>
          ) : null}
        </div>
      ) : null}

      {identity.formerNames.length > 0 ? (
        <div className="mt-4">
          <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Former names
          </h3>
          <ul className="mt-1 flex flex-col gap-1 text-sm text-slate-700">
            {/* The kind is the whole caption. A gloss beside it — "Correction" plus
                "never their name" — restated the label in worse words. */}
            {identity.formerNames.map((n) => (
              <li key={n.id} className="flex flex-wrap items-baseline gap-x-2">
                <span>{n.fullName}</span>
                <span className="text-xs text-slate-500">{describeFormerName(n)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </Card>
  );
}

// A copy of the backend's legalTransitions (internal/people/write.go). It is
// There is one lifecycle action, and it is offered on any term nobody has ended.
// No transition table: activating is not an action (a term becomes active when its
// start date arrives, with nothing written) and suspending is gone.
//
// Revive is the mirror, offered on a term that *was* ended. Note the predicate is
// isCurrent and not status — a lapsed term reads as ENDED too, and it has nothing
// to revive because nobody ended it. It is also gated on the person having no
// current term in any population, using the server's generic availability flag.
// Those rows get "Correct the last day" instead (INFRA-702).

type Engagement = NonNullable<Identity>["engagements"][number];

/** Which of the two contracted dates a cell holds. */
type DateField = "start" | "end";

/**
 * What each editable date calls itself.
 *
 * The button names the column so a reader tabbing through hears which date they are
 * about to change; the input keeps the longer, more precise field name, because a
 * header has to stay short and "Last contracted day" spells out what "Contracted end"
 * compresses — the day that was agreed, not the day somebody closed the term, which is
 * the other date in the same cell.
 */
const DATE_COPY = {
  start: {
    set: "Set the start date",
    change: "Change the start date",
    input: "First contracted day",
    save: "Save start date"
  },
  end: {
    set: "Set the contracted end",
    change: "Change the contracted end",
    input: "Last contracted day",
    save: "Save contracted end"
  }
} as const;

/**
 * A date in the table that is changed by clicking it.
 *
 * A real `<button>`, not a `<td>` with an onClick: this is a control, so it has to be
 * reachable by Tab and operable by Enter and Space, and none of that comes free from
 * a click handler on a cell.
 *
 * Looking like one is the other half, and INFRA-694 is what happens when only the
 * first half is true: the dotted underline was slate-400 under slate-600 text — a cue
 * fainter than the content it marks — with no tooltip and nothing else in this console
 * edited this way, so the dates read as plain text and an operator spent half a minute
 * hunting for them. The underline is now no lighter than the date it sits under, and a
 * hover says what a click does.
 *
 * An unset date is still a target, and says in words what it would set rather than
 * showing an em dash. That is the same sentence its accessible name already carried,
 * which is the point: a bare "—" was the smallest, faintest target on the page sitting
 * on exactly the rows somebody came to fill in, and it left the visible label and the
 * announced one disagreeing.
 *
 * Whether this cell is the open one is decided by the table, not here, so that only
 * one date in the whole table is ever an editor. Focus returns to the button when the
 * editor closes: the input unmounts, and without this the keyboard would be left on
 * `<body>` halfway down a table.
 */
function EditableDate({
  field,
  value,
  editable,
  editing,
  draft,
  busy,
  onOpen,
  onDraft,
  onSave,
  onCancel
}: {
  field: DateField;
  value: string | null | undefined;
  /** False for a viewer who may not write this term, and for a date this term's state puts beyond editing. */
  editable: boolean;
  editing: boolean;
  draft: string;
  busy: boolean;
  onOpen: () => void;
  onDraft: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  const copy = DATE_COPY[field];
  const button = useRef<HTMLButtonElement>(null);
  const wasEditing = useRef(false);
  useEffect(() => {
    // Only when closing this editor left the keyboard nowhere.
    //
    // The activeElement check is not defensive, it is the whole condition. Opening a
    // second date closes this one, and this effect runs *after* the new input has
    // autofocused — so an unconditional focus() here would drag the operator back to
    // the row they just left, every time. Body means the input unmounted with nothing
    // taking over, which is Save, Cancel and Escape; anything else means somebody is
    // already somewhere, and that is where they should stay.
    if (wasEditing.current && !editing && document.activeElement === document.body) {
      button.current?.focus();
    }
    wasEditing.current = editing;
  }, [editing]);

  // Plain text, and it has to be: a disabled button still reads as a control that is
  // temporarily unavailable, where the truth for a reader is that this date is simply
  // not theirs to change.
  //
  // Nothing at all when there is no date, rather than a dash. A dash earns its place
  // in the editable branch below, where it is the only thing there is to click; here
  // it is a placeholder for a value the reader could not supply anyway. It reads
  // worst on an ended term, where the contracted end was never agreed and the dash
  // sits directly above the day the term actually ended — two lines, one of them a
  // stand-in for nothing, and no way to tell which is the real date.
  if (!editable) {
    return value ? <span>{value}</span> : null;
  }

  if (!editing) {
    return (
      <button
        ref={button}
        type="button"
        // The visible date is contained in the name, so the two agree for anyone
        // using speech input as well as for a screen reader. With no date the visible
        // label *is* the name, which is the same rule arrived at from the other side.
        aria-label={value ? `${copy.change}, ${value}` : copy.set}
        // The hover, and the one thing the name cannot carry: the name says which date
        // this is, the tooltip says that it is clicked rather than read. Worded so it
        // repeats none of the name, since a screen reader announces both.
        title={value ? "Click to change this date" : "Click to set this date"}
        onClick={onOpen}
        // decoration-slate-600 is not a free choice: it matches the `text-slate-600`
        // of the cells this sits in, and a cue lighter than its own content is what
        // INFRA-694 was. The two are read off the DOM and compared by "marks an
        // editable date with a cue no fainter than the date itself", so lightening
        // either one fails a build rather than quietly undoing this.
        className="-mx-1 rounded px-1 text-left underline decoration-dotted decoration-slate-600 underline-offset-4 hover:bg-slate-100 hover:decoration-slate-900 focus:outline-none focus:ring-1 focus:ring-slate-500"
      >
        {value ?? copy.set}
      </button>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1">
      <Input
        type="date"
        // All this field tells a screen reader is which date it is, so the name has
        // to carry the whole distinction between the two.
        aria-label={copy.input}
        // The editor is opened by a click on the button that this replaces, so
        // focus has to be moved into it or the keyboard is left nowhere.
        autoFocus
        value={draft}
        // No bounds in either direction. A future contracted end is the point of
        // the field, a future start date is what upcoming means, and a past one of
        // either is an ordinary correction.
        onChange={(ev) => onDraft(ev.target.value)}
        onKeyDown={(ev) => {
          if (ev.key === "Enter") {
            ev.preventDefault();
            onSave();
          }
          if (ev.key === "Escape") {
            ev.preventDefault();
            onCancel();
          }
        }}
        className="w-36"
      />
      {/* Named for the date they save, because the identity card above has a
          "Save" of its own and a name has to say which one this is. */}
      <Button className="px-2 py-1 text-xs" disabled={busy} aria-label={copy.save} onClick={onSave}>
        {busy ? "Saving…" : "Save"}
      </Button>
      <Button variant="secondary" className="px-2 py-1 text-xs" disabled={busy} onClick={onCancel}>
        Cancel
      </Button>
    </div>
  );
}

/**
 * There is no floor on the termination date, and no ceiling.
 *
 * Backdating an offboarding — recording someone who left three weeks ago — is
 * ordinary. So is the opposite: withdrawing an offer after signing but before the
 * first day, whose termination date is legitimately *earlier* than a start date the
 * contract still names. An earlier revision of this change enforced
 * terminated_on >= start_date in both the database and the store, and it refused
 * exactly that record; both guards are gone, so the form imposes nothing either.
 */

function Engagements({ identity, viewer }: { identity: NonNullable<Identity>; viewer: Viewer }) {
  const terminate = useTerminateEngagement();
  const revive = useReviveEngagement();
  const setEndDate = useSetEngagementEndDate();
  const setStartDate = useSetEngagementStartDate();
  const correctLastDay = useSetEngagementTerminationDate();
  const [pendingTermination, setPendingTermination] = useState<Engagement | null>(null);
  // Which single date in the whole table is open for editing, and the value being
  // typed into it. One piece of state for the table rather than one per row: shared
  // state rendered into every row would edit them all at once, and a copy per row
  // would let two dates be half-edited at the same time with no way to tell which
  // one a Save belongs to.
  const [editingDate, setEditingDate] = useState<{ id: string; field: DateField } | null>(null);
  const [dateDraft, setDateDraft] = useState("");
  const [pendingRevival, setPendingRevival] = useState<Engagement | null>(null);
  // The closed term whose last day is being corrected, and the date being typed.
  // Held by the dialog rather than per row, for the reason the termination date
  // beside it is: one input bound to one piece of state, so setting a date on one
  // term cannot silently set it on another.
  const [pendingLastDay, setPendingLastDay] = useState<Engagement | null>(null);
  const [lastDayDraft, setLastDayDraft] = useState("");
  const [adding, setAdding] = useState(false);
  // Which term's attributes are being edited, held here rather than per row for the
  // reason the dates above are.
  //
  // The id rather than the row, so the open panel survives a refetch of the identity
  // and keeps the draft in its boxes. What it must *not* do is treat the refetched
  // term as the thing its patch is a diff against — see `opened` in the panel, and
  // INFRA-709 for what that cost. After a save that half-succeeded — attributes
  // written, beam detail refused — the panel offers to send only the half that is
  // still missing, because the successful half advanced its own baseline.
  const [editingTermId, setEditingTermId] = useState<string | null>(null);

  // Held here rather than per row because the dialog owns it: the previous version
  // rendered a date input in every row bound to one piece of state, so setting a
  // date on one engagement silently set it on the other.
  const [effectiveDate, setEffectiveDate] = useState(businessDate);

  const isEditingDate = (e: Engagement, field: DateField) =>
    editingDate?.id === e.id && editingDate.field === field;

  const openDate = (e: Engagement, field: DateField) => {
    setDateDraft((field === "start" ? e.startDate : e.endDate) ?? "");
    setEditingDate({ id: e.id, field });
  };

  const saveDate = (e: Engagement, field: DateField) => {
    // An empty box clears the date. Sent as null rather than omitted, because
    // "make this open-ended again" — or "it has not been agreed after all" — is a
    // request, not an absent field.
    const value = dateDraft === "" ? null : dateDraft;
    // Closes the editor only if it is still the one that was saved. A bare
    // setEditingDate(null) would close whatever is open by the time the response
    // lands: the mutation's isPending is shared across rows, so an operator who
    // clicked another date while this save was in flight would have that editor —
    // and the draft in it — shut by a reply to a request about a different row.
    const close = {
      onSuccess: () =>
        setEditingDate((open) => (open?.id === e.id && open.field === field ? null : open))
    };
    if (field === "start") {
      setStartDate.mutate({ engagementId: e.id, identityId: identity.id, startDate: value }, close);
    } else {
      setEndDate.mutate({ engagementId: e.id, identityId: identity.id, endDate: value }, close);
    }
  };

  const currentTerms = identity.engagements.filter((e) => e.isCurrent);
  // Availability comes from all populations on the server. The visible engagement
  // list can omit a current term outside this viewer's readable populations.
  const addablePopulations = identity.canAddEngagement ? viewer.writablePopulations : [];
  const additionBlocked = viewer.writablePopulations.length > 0 && !identity.canAddEngagement;

  return (
    <Card
      title="Engagements"
      actions={
        addablePopulations.length > 0 && !adding ? (
          <Button variant="secondary" onClick={() => setAdding(true)}>
            Add an engagement
          </Button>
        ) : null
      }
    >
      {adding && addablePopulations.length > 0 ? (
        <AddEngagementForm
          identity={identity}
          populations={addablePopulations}
          onDone={() => setAdding(false)}
        />
      ) : null}

      {additionBlocked ? <AddEngagementBlocked terms={currentTerms} /> : null}

      <Table
        head={[
          "Population",
          "Title",
          "Entity",
          "Subcategory",
          "Status",
          "Start date",
          "Contracted end",
          ""
        ]}
      >
        {identity.engagements.map((e) => {
          const mayWrite = canWrite(viewer, e.population);
          const mayAct = e.isCurrent && mayWrite;
          const editing = editingTermId === e.id;
          const firm = describeEngagementFirm(e);
          return (
            // A Fragment because the editor is a second row of the same table rather
            // than a panel above it. Above the table it could only ever describe one
            // term while sitting over all of them, and a person holding two — one per
            // population — had no way to tell which one it had opened.
            <Fragment key={e.id}>
              <tr className="border-b border-slate-100">
                {/* The firm sits under the population rather than in a ninth column,
                  and under *this* cell rather than beside any other, because the
                  population is what decides whether a firm exists at all: the store
                  refuses one on an on-demand contractor, so "a partner, through Smith,
                  Jones & Co" is one fact and not two. The same relationship the
                  Subcategory cell already renders this way for a Beam's detail.

                  Not a column. This row went from six buttons to three and from one
                  date to two because it was cluttered, and a ninth header would spend
                  that back on a field that is blank for every on-demand contractor and
                  has, on a person's own page, no column to be scanned down — there is
                  one row per population here, at most two. Scanning *by* firm is the
                  organizations roster's job, and its drill-down already answers it. */}
                <td className="px-2 py-2">
                  {humanPopulation(e.population)}
                  {firm ? <span className="block text-xs text-slate-500">{firm}</span> : null}
                </td>
                <td className="px-2 py-2">
                  {e.title}
                  <span className="mt-0.5 block text-xs text-slate-600">
                    Department: {e.department?.name ?? "Not recorded"}
                    {e.department?.archivedAt ? " (archived)" : ""}
                  </span>
                  {/* Under the title rather than in a column of its own, for the
                    reason the firm sits under the population: this row has eight
                    headers already, and a ninth holding a link for the few terms
                    that have one would be blank down most of the table. A term with
                    none says nothing at all — there is no work implied by not having
                    recorded a contract link, so a dash here would be noise.

                    Rendered as an anchor without re-checking the value. The store
                    accepts nothing but an http or https address on either write path
                    (TestContractLinkMustBeAWebAddress), which is what makes an href
                    built from it safe; checking again here would put the rule in two
                    places and let the copies drift. */}
                  {e.population === "PARTNER" ? (
                    <span className="mt-0.5 block text-xs text-slate-600">
                      DRI: {e.dri?.name ?? "Not recorded"}
                    </span>
                  ) : null}
                  {e.contractLink ? (
                    <a
                      className="mt-0.5 block text-xs text-slate-600 underline hover:text-slate-900"
                      href={e.contractLink}
                      target="_blank"
                      rel="noreferrer"
                    >
                      Contract
                    </a>
                  ) : null}
                </td>
                <td className="px-2 py-2 text-slate-600">{e.entity?.code ?? "—"}</td>
                <td className="px-2 py-2 text-slate-600">
                  {e.subcategory?.name ?? "—"}
                  {/* Only a Beam has these, so only a Beam's row says anything about
                    them — including that they are not recorded yet, which is work to
                    do rather than a property of the term. */}
                  {isBeamSubcategory(e.subcategory) ? (
                    <span className="block text-xs text-slate-500">
                      {describeBeamDetail(e.beamDetail)}
                    </span>
                  ) : null}
                </td>
                {/* What is left once the dates have columns of their own: where the
                  term sits in time, and the one thing about it that is work rather
                  than a fact. Both "ended …" and "ends …" used to be repeated here,
                  where neither could be read against the contracted end sitting two
                  columns away — which is what made a term starting on the 4th and
                  closed on the 3rd look like a contradiction. */}
                <td className="px-2 py-2">
                  <Badge tone={statusTone(e.status)}>{humanStatus(e.status)}</Badge>
                  {/* A contracted end that passed with nobody closing the term out. Not
                    a date and not a status either — it is an offboarding somebody
                    still has to do, which is why it stays beside the position rather
                    than moving into the contracted-end cell. */}
                  {e.hasLapsed ? (
                    <span className="ml-1 text-xs font-medium text-amber-700">not closed out</span>
                  ) : null}
                </td>
                <td className="px-2 py-2 text-slate-600">
                  <EditableDate
                    field="start"
                    value={e.startDate}
                    // Not gated on the term being current, unlike the contracted end
                    // beside it: the start is in neither half of the pair endedEarly is
                    // computed from, so correcting the first day on a term that ended
                    // last year rewrites nothing the record claims happened.
                    editable={mayWrite}
                    editing={isEditingDate(e, "start")}
                    draft={dateDraft}
                    busy={setStartDate.isPending}
                    onOpen={() => openDate(e, "start")}
                    onDraft={setDateDraft}
                    onSave={() => saveDate(e, "start")}
                    onCancel={() => setEditingDate(null)}
                  />
                </td>
                <td className="px-2 py-2 text-slate-600">
                  <EditableDate
                    field="end"
                    value={e.endDate}
                    // When a term is due to run out is an attribute of the term, so it
                    // needs write access rather than the lifecycle permission — and it
                    // stops being editable once somebody has ended the term, because
                    // from then on it is one half of the pair endedEarly is computed
                    // from and rewriting it would change what the record claims.
                    editable={e.isCurrent && mayWrite}
                    editing={isEditingDate(e, "end")}
                    draft={dateDraft}
                    busy={setEndDate.isPending}
                    onOpen={() => openDate(e, "end")}
                    onDraft={setDateDraft}
                    onSave={() => saveDate(e, "end")}
                    onCancel={() => setEditingDate(null)}
                  />
                  {/* The termination, under the contracted end rather than instead of
                    it. Two different facts — what was agreed, and what somebody
                    actually did — and endedEarly is derived from the pair, so
                    collapsing them into one value would erase the difference it is
                    made of.

                    Rendered as text and never as a box. The rule is not that
                    terminatedOn is never typed — Correct the last day takes a typed
                    date — it is that every path which writes it is a confirmed action
                    leaving an audit entry that says a human decided something. A box
                    in this cell would be the one path with nothing recording that
                    anybody meant it. That held for two writers, said something false
                    about the console once the third arrived, and is now pinned both
                    ways: the writers are enumerated by the store's
                    TestTheWritersOfTerminatedOnAreStillTheseThree, and each of the
                    three has a test here that nothing is sent until its dialog is
                    confirmed.

                    Named, and in the word the history panel below already uses for
                    this column. A header can only name one of the two dates in this
                    cell, so while this line was a bare lowercase date it sat under a
                    header belonging to the other one — which is how somebody who had
                    just set a date could not tell which of the two they had set. */}
                  {e.terminatedOn ? (
                    <span className="mt-0.5 block text-xs font-medium text-slate-600">
                      {e.endedEarly
                        ? `Ended early on ${e.terminatedOn}`
                        : `Ended on ${e.terminatedOn}`}
                    </span>
                  ) : null}
                </td>
                <td className="px-2 py-2">
                  <div className="flex flex-wrap items-center gap-1">
                    {e.isCurrent ? (
                      <Button
                        variant="danger"
                        disabled={!mayAct || terminate.isPending}
                        onClick={() => {
                          setEffectiveDate(businessDate());
                          setPendingTermination(e);
                        }}
                      >
                        End engagement
                      </Button>
                    ) : null}
                    {/* One Edit, covering everything the term records — including a
                      Beam's number and type, which had a second button of their own
                      until they moved inside this form.

                      Not gated on the term being current: this corrects what the
                      term *was*, and the wrong firm on a term that ended last year is
                      exactly the record worth fixing. Unlike the contracted end,
                      which is history once somebody has ended the term. */}
                    {mayWrite ? (
                      <Button
                        variant="secondary"
                        aria-expanded={editing}
                        onClick={() => setEditingTermId(editing ? null : e.id)}
                      >
                        Edit
                      </Button>
                    ) : null}
                    {/* Revive uses the same global availability as a new engagement. */}
                    {!e.isCurrent && mayWrite && identity.canAddEngagement ? (
                      <Button
                        variant="secondary"
                        disabled={revive.isPending}
                        onClick={() => setPendingRevival(e)}
                      >
                        Revive
                      </Button>
                    ) : null}
                    {/* A button rather than a date box in the cell above, deliberately.
                      terminatedOn is changed by actions that record who decided
                      something, and typing into a cell is the one way to write it with
                      no such record — see the comment on that cell. Keyed on the date
                      itself rather than on !isCurrent: this corrects a recorded last
                      day, so it is offered exactly where there is one. */}
                    {e.terminatedOn && mayWrite ? (
                      <Button
                        variant="secondary"
                        disabled={correctLastDay.isPending}
                        onClick={() => {
                          setLastDayDraft(e.terminatedOn ?? "");
                          setPendingLastDay(e);
                        }}
                      >
                        Correct the last day
                      </Button>
                    ) : null}
                  </div>
                </td>
              </tr>
              {editing ? (
                <tr className="border-b border-slate-100 bg-slate-50">
                  {/* Spanning every column so the editor reads as an expansion of the
                    whole row rather than of one cell. */}
                  <td className="px-2 py-3" colSpan={8}>
                    <EditEngagementForm
                      // Keyed on the engagement so switching rows starts from that
                      // row's values rather than keeping the last one's in the boxes.
                      // Not on anything that changes when the row refetches, or a save
                      // would blow away the draft it just half-committed.
                      key={e.id}
                      identityId={identity.id}
                      engagement={e}
                      onDone={() => setEditingTermId(null)}
                    />
                  </td>
                </tr>
              ) : null}
            </Fragment>
          );
        })}
      </Table>

      <ConfirmDialog
        open={pendingTermination !== null}
        title="End this engagement?"
        confirmLabel="End engagement"
        busy={terminate.isPending}
        // Clearing the date box would otherwise send an empty Date scalar and fail
        // in the API rather than in the form.
        confirmDisabled={effectiveDate === "" || effectiveDate > businessDate()}
        onCancel={() => setPendingTermination(null)}
        onConfirm={() => {
          const target = pendingTermination;
          if (!target) {
            return;
          }
          terminate.mutate(
            {
              identityId: identity.id,
              population: target.population,
              terminatedOn: effectiveDate
            },
            { onSuccess: () => setPendingTermination(null) }
          );
        }}
      >
        <p>
          This ends {identity.legalFullName || "this person"}&apos;s{" "}
          <strong>{humanPopulation(pendingTermination?.population ?? "PARTNER")}</strong>{" "}
          engagement. They stop counting as current, and a new engagement is how they come back.
          Their system access goes with their last current engagement, which takes them out of Okta
          when export is configured. Adding an engagement or reviving this one does not give it
          back; only Restore system access at the top of the page does.
        </p>
        <div className="mt-3">
          <Field
            label="Last working day"
            required
            hint={
              effectiveDate === ""
                ? "Required"
                : effectiveDate > businessDate()
                  ? "Cannot be in the future — close this and change the contracted end on the row instead"
                  : pendingTermination?.endDate
                    ? `The contracted end is ${pendingTermination.endDate}, which this does not change`
                    : undefined
            }
          >
            <Input
              type="date"
              value={effectiveDate}
              // Backdating is ordinary, so there is no floor. The ceiling is real:
              // this field means the term is over, and a future value would mark
              // someone who is still working as already gone.
              max={businessDate()}
              onChange={(ev) => setEffectiveDate(ev.target.value)}
              className="w-40"
            />
          </Field>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Reviving clears the recorded ending and leaves contracted dates unchanged. It requires no
          current engagement in any population and cannot introduce an overlap with this
          population's history. Use Correct the last day to fix a date while keeping the term ended.
          Every step is recorded in the history below.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={pendingRevival !== null}
        title="Revive this engagement?"
        confirmLabel="Revive"
        tone="primary"
        busy={revive.isPending}
        onCancel={() => setPendingRevival(null)}
        onConfirm={() => {
          const target = pendingRevival;
          if (!target) {
            return;
          }
          revive.mutate(
            { engagementId: target.id, identityId: identity.id },
            { onSuccess: () => setPendingRevival(null) }
          );
        }}
      >
        {/* Not "restores the status and end date it had", which is what this said and
            was never true of the mutation it describes: clearing the day the term
            ended is the whole operation, and the contracted dates were never
            overwritten for it to restore. Corrected here rather than left beside the
            correction dialog below, which sends a reader to this button. */}
        <p>
          This clears the day the closed{" "}
          <strong>{humanPopulation(pendingRevival?.population ?? "PARTNER")}</strong> engagement
          ended, making it current again. Its contracted dates are untouched, so the term goes back
          to whatever they say — including not yet started, if the first day is still ahead. Their
          system access stays removed until somebody uses Restore system access at the top of the
          page.
        </p>
        <p className="mt-2 text-xs text-slate-500">
          For correcting a deactivation that should not have happened. To fix a last day that was
          simply recorded wrong, use Correct the last day instead and the term stays ended. If the
          person genuinely left and has come back, add a new engagement so the gap stays in the
          record.
        </p>
      </ConfirmDialog>

      <ConfirmDialog
        open={pendingLastDay !== null}
        title="Correct the last day?"
        confirmLabel="Save last day"
        busy={correctLastDay.isPending}
        // The refusals a draft can be tested against on its own — empty, later than
        // today, unchanged — answered here because the operator can see the answer
        // before pressing anything. Unchanged is the one worth naming: the backend
        // writes nothing at all for it, and a "Last day corrected" toast for a save
        // that did not happen is the one thing that would make this dialog lie.
        //
        // Not every refusal the backend makes, and this deliberately does not say how
        // many there are — the sentence that did went false the next time one was
        // added. Overlapping another of this person's terms is a rule about the whole
        // set of them, and re-deriving it here would be a second implementation that
        // can disagree with the first at the edges; the operator meets that one as the
        // backend's own message, with this dialog still open to pick another day.
        // The store's TestEveryRefusalThisCorrectionMakesIsOnThisList names this
        // dialog, so a refusal this form cannot pre-empt fails a build rather than
        // arriving at an operator the copy told it would not.
        confirmDisabled={
          lastDayDraft === "" ||
          lastDayDraft > businessDate() ||
          lastDayDraft === pendingLastDay?.terminatedOn
        }
        onCancel={() => setPendingLastDay(null)}
        onConfirm={() => {
          const target = pendingLastDay;
          if (!target) {
            return;
          }
          correctLastDay.mutate(
            { engagementId: target.id, identityId: identity.id, terminatedOn: lastDayDraft },
            { onSuccess: () => setPendingLastDay(null) }
          );
        }}
      >
        <p>
          This changes the day {identity.legalFullName || "this person"}&apos;s{" "}
          <strong>{humanPopulation(pendingLastDay?.population ?? "PARTNER")}</strong> engagement is
          recorded as having ended. The term stays ended, and the contracted dates are left as they
          are.
        </p>
        <div className="mt-3">
          <Field
            label="Last working day"
            required
            hint={
              lastDayDraft === ""
                ? "Required"
                : lastDayDraft > businessDate()
                  ? "Cannot be in the future — a term cannot have ended on a day that has not happened"
                  : lastDayDraft === pendingLastDay?.terminatedOn
                    ? "Unchanged — this is the day already recorded"
                    : `Currently recorded as ${pendingLastDay?.terminatedOn}`
            }
          >
            <Input
              type="date"
              value={lastDayDraft}
              max={businessDate()}
              onChange={(ev) => setLastDayDraft(ev.target.value)}
              className="w-40"
            />
          </Field>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Correct a recorded date while keeping the term ended. Revive reopens it and requires no
          current engagement in any population. This correction is recorded as its own event in the
          history below.
        </p>
      </ConfirmDialog>
    </Card>
  );
}

/** Explain the global current-term rule without exposing hidden engagements. */
function AddEngagementBlocked({ terms }: { terms: Engagement[] }) {
  // Amber for a lapsed blocker, matching the row's own flag and the edit form's
  // warning: that one is undone work rather than a settled fact about the record.
  const lapsed = terms.some((e) => e.hasLapsed);
  const tone = lapsed
    ? {
        box: "border-amber-300 bg-amber-50",
        head: "text-amber-900",
        body: "text-amber-900",
        note: "text-amber-800"
      }
    : {
        box: "border-slate-200 bg-slate-50",
        head: "text-slate-800",
        body: "text-slate-700",
        note: "text-slate-500"
      };

  return (
    <div className={`mb-4 rounded-md border p-3 ${tone.box}`}>
      <h3 className={`text-sm font-semibold ${tone.head}`}>No engagement can be added</h3>
      <p className={`mt-1 text-sm ${tone.body}`}>
        Only one engagement can be current at a time across all populations.
      </p>
      {terms.length === 0 ? (
        <p className={`mt-1 text-sm ${tone.body}`}>
          This person already has a current engagement. An operator with access to it must end it
          before another can be added.
        </p>
      ) : null}
      <ul className={`mt-1 flex flex-col gap-1 text-sm ${tone.body}`}>
        {terms.map((e) => (
          <li key={e.id}>
            {humanPopulation(e.population)} already has a current engagement
            {/* Branching on hasLapsed alone, and dropping only the date when the field
                is null. The date is in fact guaranteed here — the backend's HasLapsed
                returns false without one — but keying the whole sentence on the pair
                would answer an unreachable null by quietly telling an operator their
                lapsed term is an ordinary current one, which is the wrong way for a
                belt-and-braces check to fail. */}
            {e.hasLapsed
              ? `. The one below is past its contracted end${
                  e.endDate ? ` (${e.endDate})` : ""
                } and nobody has closed it out, so it still counts as the current one.`
              : ", and there is already one below."}
          </li>
        ))}
      </ul>
      <p className={`mt-2 text-xs ${tone.note}`}>
        End the current engagement before adding another. Operators with permission can use
        &ldquo;End engagement&rdquo; on its row. Use &ldquo;Edit&rdquo; for corrections to the
        existing term.
      </p>
    </div>
  );
}

/**
 * Correcting what an existing term is: its entity, subcategory, firm, title, and —
 * for a Beam — its number and type.
 *
 * The gap this closes: those were written once by the create form and editable by
 * nothing, so a term recorded through the wrong firm could only be fixed by ending
 * it and opening a new one — which fabricates a history that did not happen.
 *
 * The Beam fields were a second button and a second panel of their own until this
 * form absorbed them, which is what an operator asked for: two buttons named "Edit"
 * and "Beam detail" on one row is one question ("which of these do I want?") that
 * nobody should have had to answer. They follow the *chosen* subcategory rather than
 * the stored one, so moving a term onto Beam offers them in the same edit.
 *
 * Population is shown and not editable, which is the one thing here worth reading
 * the comment for. It keys the index that makes "the current term" a single row, it
 * is the filter every scoped read is built on, and the legal field set differs by
 * it. Someone moving between populations gets a *new* term, which is the truer record
 * anyway. Stated in the panel rather than left as a missing field, so an operator
 * looking for it finds the answer instead of assuming the console forgot.
 */
function EditEngagementForm({
  identityId,
  engagement,
  onDone
}: {
  identityId: string;
  engagement: Engagement;
  onDone: () => void;
}) {
  // Both quiet, because this form presses one button and sends up to two mutations:
  // each announcing itself would report one action twice, and a half-failure as a
  // green "Engagement saved" followed by a red error — the misleading half first.
  // What actually happened is said once, below, by the code that knows.
  const save = useUpdateEngagement({ quiet: true });
  const saveBeamDetail = useSetEngagementBeamDetail({ quiet: true });
  const remove = useDeleteEngagement();
  const [removing, setRemoving] = useState(false);
  const { data: lookups } = useLookups();

  /**
   * What the panel opened with, which is what the patch below is a diff against.
   *
   * It has to be held rather than read back off the props, and that is INFRA-709 — the
   * same defect INFRA-707 closed on the organization row. This panel is keyed on the
   * engagement id precisely so that a refetch does *not* remount it, and the identity
   * is re-read by every write on this screen: a date clicked on the row above, the
   * identity card's own Save, a term ended further down the table, the business-date
   * rollover. So the props move under an open panel while the boxes do not, and a
   * field nobody touched starts *differing* from its own prop — which is exactly what
   * the patch treats as a deliberate replacement. The lost update the patch exists to
   * prevent would come back through the patch itself.
   *
   * One `useState` initialiser rather than a `useRef`, so the freeze is the same
   * mechanism as the boxes below rather than a second one.
   *
   * Advanced rather than frozen for good: a successful attributes save makes the
   * values it sent what the server now holds, so those become the new baseline. That
   * is what leaves a half-saved form offering only the half still missing — see
   * `submit`.
   *
   * `organizationsForEditing` and `movingOffBeam` read the live prop deliberately and
   * must keep doing so: each is about what the term *is*, not about what this operator
   * opened it holding.
   */
  const [opened, setOpened] = useState({
    entityId: engagement.entity?.id ?? "",
    departmentId: engagement.department?.id ?? "",
    subcategoryId: engagement.subcategory?.id ?? "",
    partnerOrganizationId: engagement.partnerOrganization?.id ?? "",
    title: engagement.title,
    driId: engagement.dri?.id ?? "",
    contractLink: engagement.contractLink ?? "",
    beamNumber: engagement.beamDetail?.beamNumber ?? "",
    beamType: (engagement.beamDetail?.beamType ?? "") as BeamType | ""
  });

  const [entityId, setEntityId] = useState(opened.entityId);
  const [departmentId, setDepartmentId] = useState(opened.departmentId);
  const [subcategoryId, setSubcategoryId] = useState(opened.subcategoryId);
  const [partnerOrganizationId, setPartnerOrganizationId] = useState(opened.partnerOrganizationId);
  const [title, setTitle] = useState(opened.title);
  const [driId, setDriId] = useState(opened.driId);
  // "" is "none recorded", which is also what clears one: the mutation reads an empty
  // string as a clear, and the comparison below only sends it when there was a link.
  const [contractLink, setContractLink] = useState(opened.contractLink);
  const [beamNumber, setBeamNumber] = useState(opened.beamNumber);
  const [beamType, setBeamType] = useState<BeamType | "">(opened.beamType);
  // Set when the attributes were written and the beam detail was not. Held rather
  // than toasted, because it has to stay on screen next to the boxes that still hold
  // the half nobody saved.
  const [halfSaved, setHalfSaved] = useState(false);

  const isODC = engagement.population === "ON_DEMAND_CONTRACTOR";

  // Only this term's own population, because it cannot change. The create form filters
  // by a population the operator is choosing; here the population is a fact.
  const subcategories = (lookups?.needNpdSubcategories ?? []).filter(
    (sub) => sub.defaultPopulation === engagement.population
  );

  // Keeps the term's current firm even when archived. Without it a `<Select>` whose
  // value is not among its options renders as unselected, and an operator here to fix
  // the title would submit a cleared organization — see organizationsForEditing.
  const organizations = organizationsForEditing(
    lookups?.externalPartnerOrganizations,
    engagement.partnerOrganization?.id
  );

  // From the lookup list once it has landed, and from the term itself before that: a
  // Beam's fields must be on screen from the first render rather than appearing a
  // moment later when the query resolves.
  const chosenSubcategory =
    subcategories.find((sub) => sub.id === subcategoryId) ??
    (subcategoryId === engagement.subcategory?.id ? engagement.subcategory : undefined);
  // The Beam section follows what is *chosen*, on the same rule the create form uses:
  // these two fields belong to that one subcategory, so they appear and disappear as
  // the picker beside them moves.
  const isBeam = isBeamSubcategory(chosenSubcategory);
  const movingOffBeam =
    isBeamSubcategory(engagement.subcategory) &&
    !isBeam &&
    engagement.beamDetail !== null &&
    engagement.beamDetail !== undefined;

  // Blank is refused for a partner and meaningless for an on-demand contractor,
  // whose title comes from the subcategory.
  const incomplete = !entityId || !subcategoryId || (!isODC && !title.trim());

  // Only the attributes this operator actually changed.
  //
  // The mutation leaves an omitted field alone, and sending all four every time would
  // defeat that: someone correcting a title would also submit the entity, subcategory
  // and firm as they stood when the form opened, quietly reverting another operator's
  // edit made in between. The audit log would record the reversion under the wrong
  // person, as an edit they did not make.
  //
  // Diffed against `opened` and never against the props — see above, and INFRA-709 for
  // the harm when it was the props. Clearing stays expressible either way, which is
  // what sending everything was for: an emptied box differs from a non-empty original
  // and is sent as "", while a box that was empty when the panel opened is not sent at
  // all.
  const changed: EngagementAttributesInput = {};
  if (departmentId !== opened.departmentId) {
    changed.departmentId = departmentId;
  }
  if (entityId !== opened.entityId) {
    changed.entityId = entityId;
  }
  if (subcategoryId !== opened.subcategoryId) {
    changed.subcategoryId = subcategoryId;
  }
  // Both omitted entirely for an on-demand contractor — by population rather than by
  // whether a box is empty, since the form does not show either field to them.
  if (!isODC) {
    // Sent as the box holds it, not filtered through `|| undefined`: an empty string
    // is how "no firm after all" is said, and the create form's idiom would turn that
    // into "leave it alone".
    if (partnerOrganizationId !== opened.partnerOrganizationId) {
      changed.partnerOrganizationId = partnerOrganizationId;
    }
    if (driId !== opened.driId) {
      changed.driId = driId;
    }
    if (title !== opened.title) {
      changed.title = title;
    }
  }
  // Outside the population branch, unlike the two above: an on-demand contractor
  // signs a contract too, so this is the one field in this form both populations get.
  // Sent as the box holds it, so an emptied box clears the link rather than being
  // read as "leave it alone".
  if (contractLink !== opened.contractLink) {
    changed.contractLink = contractLink;
  }

  // Guarded on `isBeam` rather than on the boxes being filled, the same rule the
  // create form follows: a number typed and then abandoned by moving the subcategory
  // off Beam is not a number anybody asked to record.
  //
  // Against `opened` for the reason the attributes are, and the reach here is longer:
  // this mutation replaces the pair rather than patching it, so a beam number that
  // arrived under an open panel would make an untouched, empty box "differ" and send
  // both fields as empty — an explicit clear of a number this operator never saw.
  const beamChanged = isBeam && (beamNumber !== opened.beamNumber || beamType !== opened.beamType);

  const busy = save.isPending || saveBeamDetail.isPending;
  const nothingToSave = Object.keys(changed).length === 0 && !beamChanged;

  /**
   * Two mutations behind one button, sent one after the other.
   *
   * The order is a constraint and not a preference: setEngagementBeamDetail refuses a
   * term that is not a Beam, so a term being moved *onto* Beam has to arrive there
   * before its number can be recorded. Attributes first, always.
   *
   * Which makes the interesting failure the second one, with the first already
   * committed — and there is no transaction spanning two mutations to hide behind. It
   * is reported as exactly what it is: what was saved, what was not, and the panel
   * left open on the half still to send. Closing here and toasting "saved" would send
   * an operator away believing a beam number had been recorded that had not.
   */
  const submit = async () => {
    setHalfSaved(false);
    const attributes = Object.keys(changed).length > 0;
    if (attributes) {
      try {
        await save.mutateAsync({ engagementId: engagement.id, identityId, fields: changed });
      } catch {
        // Already reported by the mutation's own error toast, and nothing after it
        // ran, so the form is exactly as the operator left it.
        return;
      }
      // What was just written is what the server now holds, so it becomes the
      // baseline. Without this a half-saved panel would re-send, on the next press,
      // an attributes half it had already committed.
      setOpened((was) => ({
        ...was,
        entityId,
        departmentId,
        subcategoryId,
        // Offered to both populations, so it advances unconditionally — unlike the
        // two below, which an on-demand contractor's form never showed and never sent.
        contractLink,
        ...(isODC ? {} : { partnerOrganizationId, title, driId })
      }));
    }
    if (beamChanged) {
      try {
        await saveBeamDetail.mutateAsync({
          engagementId: engagement.id,
          identityId,
          // An emptied box clears the field: the pair replaces what is stored, so
          // leaving one out is how "there is no number after all" is said.
          detail: { beamNumber: beamNumber || undefined, beamType: beamType || undefined }
        });
      } catch {
        setHalfSaved(attributes);
        return;
      }
    }
    // One press, one report. Named for the whole thing rather than for whichever
    // half happened to be sent, because the operator pressed one button.
    toast.success("Engagement saved");
    onDone();
  };

  return (
    <div className="mb-4 rounded-md border border-slate-300 bg-slate-50 p-3">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800">Edit this engagement</h3>
        <span className="text-xs text-slate-500">
          {humanPopulation(engagement.population)}
          {engagement.startDate ? ` · from ${engagement.startDate}` : null}
        </span>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Field label="Entity" hint="The contracting and paying entity" required>
          <Select value={entityId} onChange={(e) => setEntityId(e.target.value)}>
            {(lookups?.needEntities ?? []).map((entity) => (
              <option key={entity.id} value={entity.id}>
                {entity.name}
              </option>
            ))}
          </Select>
        </Field>
        <DepartmentField
          departments={lookups?.departments}
          value={departmentId}
          onChange={setDepartmentId}
          current={engagement.department}
        />
        <Field label="Subcategory" required>
          <Select value={subcategoryId} onChange={(e) => setSubcategoryId(e.target.value)}>
            {subcategories.map((sub) => (
              <option key={sub.id} value={sub.id}>
                {sub.name}
              </option>
            ))}
          </Select>
        </Field>
        {isODC ? null : (
          <Field label="Partner organization" hint="Blank for independent individuals">
            <Select
              value={partnerOrganizationId}
              onChange={(e) => setPartnerOrganizationId(e.target.value)}
            >
              <option value="">None</option>
              {organizations.map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                  {org.archivedAt ? " (retired)" : ""}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {isODC ? (
          <p className="self-center text-xs text-slate-500">
            The title is the subcategory for an on-demand contractor, and is recorded from it — so
            changing the subcategory renames the term too.
          </p>
        ) : (
          <Field label="Title" required>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
        )}
        {isODC ? null : (
          <PartnerDRIField value={driId} onChange={setDriId} legacy={!opened.driId} />
        )}
        <Field
          label="Contract link"
          hint="A link to the signed contract where it lives. Nothing is uploaded here."
        >
          <Input
            type="url"
            placeholder="https://…"
            value={contractLink}
            onChange={(e) => setContractLink(e.target.value)}
          />
        </Field>
        {/* The two fields no other subcategory has, in the form that edits every
            other field — which is why there is no longer a "Beam detail" button
            beside "Edit". Neither is required: a Beam is usually onboarded before
            the number is issued, which is the whole reason this is editable after
            the term exists at all. */}
        {isBeam ? (
          <BeamFields
            beamNumber={beamNumber}
            beamType={beamType}
            onBeamNumber={setBeamNumber}
            onBeamType={setBeamType}
          />
        ) : null}
      </div>

      {/* Said before the save rather than after the refusal. The server refuses this
          too, and has to; showing it here means the operator learns the order that
          works instead of reading an error about fields that have just vanished from
          their screen — they disappeared because the subcategory moved, which is the
          very edit being refused. */}
      {movingOffBeam ? (
        <p className="mt-3 rounded border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800">
          This term records a beam number, which belongs to a Beam. Put the subcategory back to{" "}
          <span className="font-medium">Beam</span>, clear the number and type in the fields that
          come back, and save — then change the subcategory. Doing it the other way round would
          throw the number away.
        </p>
      ) : null}

      {/* The honest half of a two-mutation save. Left on screen with the boxes that
          still hold the unsaved half, rather than toasted and gone. */}
      {halfSaved ? (
        <p className="mt-3 rounded border border-red-300 bg-red-50 p-2 text-xs text-red-800">
          Half of this was saved. The entity, subcategory, firm and title were written; the beam
          number and type were not — that call failed, and the boxes above still hold what you
          typed. Press <span className="font-medium">Save engagement</span> again to send it.
        </p>
      ) : null}

      {/* Where the dates are, said in the one place the operator who went looking for
          them actually arrives. "Edit" is the obvious first move for somebody after a
          start or end date, and this panel holds everything about the term except
          those two — so until now it was a dead end that printed the start date in the
          corner as unchangeable text and pointed nowhere. That is INFRA-694, and it is
          the half of it no amount of styling on the cell can reach.

          A sentence rather than the fields themselves: putting the dates in here as
          boxes would leave two live editors for one value on screen at once — this
          panel's box and the cell's own, directly above it, with separate drafts and
          nothing saying which wins — and would grow one button from two mutations to
          four with no transaction under them. The reasoning is in the PR.

          Not the helper prose #11585 cut. That was a paragraph under the card, read
          forever by everyone; this is one line inside a panel nobody opens unless they
          are already editing the term, in the same category as the population sentence
          beneath it: a field this form does not hold, and where it lives instead. */}
      <p className="mt-3 text-xs text-slate-500">
        {engagement.isCurrent
          ? "The dates are not in here: click the start date or the contracted end on the row above to change either of them."
          : "The dates are not in here: click the start date on the row above to change it. The contracted end can no longer be changed, because this engagement has been ended."}
      </p>

      <p className="mt-3 text-xs text-slate-500">
        The population cannot be changed: it decides which rules a term follows and who can see it.
        Someone moving between populations gets a new engagement instead.
      </p>

      <div className="mt-3 flex gap-2">
        <Button disabled={incomplete || movingOffBeam || nothingToSave || busy} onClick={submit}>
          {busy ? "Saving…" : "Save engagement"}
        </Button>
        <Button variant="secondary" disabled={busy} onClick={onDone}>
          Cancel
        </Button>
      </div>

      {/* Removing the term lives down here, inside the panel, and that placement is
          the decision rather than a leftover.

          Not a fourth button on the row. That row went from six buttons to three and
          declined a ninth column twice, and a delete sitting beside "End engagement"
          is the one arrangement guaranteed to be misread — two adjacent red-ish
          buttons where one records that somebody left and the other says they were
          never here. Behind the Edit panel it is two deliberate steps from the table,
          under a heading that says what it is, in the panel an operator already opens
          to correct a term. The permission is the same either way: every writer role
          holds update and lifecycle together in the population, so nothing is offered
          here that the row would have offered.

          Separated by a rule and its own heading rather than dropped in beside Save,
          because the rest of this panel edits what the term says and this removes the
          term. */}
      <div className="mt-4 border-t border-slate-200 pt-3">
        <h4 className="text-sm font-semibold text-slate-800">Remove this engagement</h4>
        <p className="mt-1 text-xs text-slate-500">
          For a term that should never have been recorded — a duplicate, a typo, a row an import
          filed against the wrong person. This deletes the record; it does not end it. If the
          engagement really happened and has finished,{" "}
          <span className="font-medium">End engagement</span> on the row above is what says so.
        </p>
        <Button
          variant="danger"
          className="mt-2"
          disabled={busy || remove.isPending}
          onClick={() => setRemoving(true)}
        >
          Remove this engagement
        </Button>
      </div>

      <ConfirmDialog
        open={removing}
        title="Remove this engagement?"
        confirmLabel="Remove engagement"
        busy={remove.isPending}
        onCancel={() => setRemoving(false)}
        onConfirm={() =>
          remove.mutate(
            { engagementId: engagement.id, identityId },
            {
              onSuccess: () => {
                setRemoving(false);
                // The row this panel is attached to has gone, so the panel closes with
                // it rather than staying open over a term that is no longer there.
                onDone();
              }
            }
          )
        }
      >
        <p>
          This <strong>deletes</strong> the {humanPopulation(engagement.population)} engagement
          {engagement.startDate ? ` from ${engagement.startDate}` : ""} — the dates, the firm, the
          title and any beam number with it.
        </p>
        {/* The distinction the whole dialog exists for, said before anything else about
            consequences: End and Remove are opposite claims about the same row. */}
        <p className="mt-2">
          It does not end the engagement. Ending records that it happened and then stopped; this
          records that it should never have been here at all.
        </p>
        <p className="mt-3 text-xs text-slate-500">
          <span className="font-medium">This cannot be undone.</span> Adding the engagement again
          creates a different one, with its own history. What was removed is kept in the history
          below — that entry is the only surviving record of the term.
        </p>
        {/* Stated as the rule rather than pre-computed for this term: whether the
            person holds another engagement is a question about their whole record, and
            this screen sees only the populations the viewer can read. Re-deriving it
            here would be a second implementation that disagrees with the server's on
            exactly the dual-population case, which is the reasoning the Correct the
            last day dialog gives for leaving its own set-wide refusal to the backend. */}
        <p className="mt-2 text-xs text-slate-500">
          A person's last engagement cannot be removed — nobody would be able to see them
          afterwards. Correct the term instead, or end it.
        </p>
      </ConfirmDialog>
    </div>
  );
}

/**
 * A new engagement for someone already in the system — a rehire, or someone picking
 * up work in the other population. The point is not re-entering identity fields:
 * everything here belongs to the engagement, and the person is already chosen.
 */
function AddEngagementForm({
  identity,
  populations,
  onDone
}: {
  identity: NonNullable<Identity>;
  populations: Viewer["writablePopulations"];
  onDone: () => void;
}) {
  const add = useAddEngagement();
  const { data: lookups } = useLookups();

  const [population, setPopulation] = useState(populations[0] ?? "PARTNER");
  const [entityId, setEntityId] = useState("");
  const [departmentId, setDepartmentId] = useState("");
  const [subcategoryId, setSubcategoryId] = useState("");
  const [partnerOrganizationId, setPartnerOrganizationId] = useState("");
  const [title, setTitle] = useState("");
  const [driId, setDriId] = useState("");
  const [contractLink, setContractLink] = useState("");
  const [beamNumber, setBeamNumber] = useState("");
  const [beamType, setBeamType] = useState<BeamType | "">("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Subcategories belong to one population; offering the other side's would route
  // the person into the wrong database, and the backend rejects it anyway.
  const subcategories = (lookups?.needNpdSubcategories ?? []).filter(
    (sub) => sub.defaultPopulation === population
  );

  // The same two fields the create form drops for this population: the partner
  // organization is the firm a partner is engaged through, and the title is the
  // subcategory, which the backend records from it. Both are refused server-side.
  const isODC = population === "ON_DEMAND_CONTRACTOR";

  // The Beam section, on the same rule as the create form: it follows the chosen
  // subcategory rather than the population, because these two fields belong to that
  // one subcategory. Neither is required.
  const isBeam = isBeamSubcategory(subcategories.find((sub) => sub.id === subcategoryId));

  const incomplete = !entityId || !subcategoryId || (!isODC && (!title.trim() || !driId));

  return (
    <div className="mb-4 rounded-md border border-slate-300 bg-slate-50 p-3">
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-800">Add an engagement</h3>
        <span className="text-xs text-slate-500">
          <span className="text-red-600">*</span> required
        </span>
      </div>
      <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
        <Field label="Population" required>
          <Select
            value={population}
            onChange={(e) => {
              setPopulation(e.target.value as typeof population);
              // The chosen subcategory belongs to the old population, so keeping it
              // would submit a pairing the backend refuses.
              setSubcategoryId("");
            }}
          >
            {populations.map((p) => (
              <option key={p} value={p}>
                {humanPopulation(p)}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Entity" hint="The contracting and paying entity" required>
          <Select value={entityId} onChange={(e) => setEntityId(e.target.value)}>
            <option value="">Choose…</option>
            {(lookups?.needEntities ?? []).map((entity) => (
              <option key={entity.id} value={entity.id}>
                {entity.name}
              </option>
            ))}
          </Select>
        </Field>
        <DepartmentField
          departments={lookups?.departments}
          value={departmentId}
          onChange={setDepartmentId}
        />
        <Field label="Subcategory" required>
          <Select value={subcategoryId} onChange={(e) => setSubcategoryId(e.target.value)}>
            <option value="">Choose…</option>
            {subcategories.map((sub) => (
              <option key={sub.id} value={sub.id}>
                {sub.name}
              </option>
            ))}
          </Select>
        </Field>
        {isODC ? null : (
          <Field label="Partner organization" hint="Blank for independent individuals">
            <Select
              value={partnerOrganizationId}
              onChange={(e) => setPartnerOrganizationId(e.target.value)}
            >
              <option value="">None</option>
              {assignableOrganizations(lookups?.externalPartnerOrganizations).map((org) => (
                <option key={org.id} value={org.id}>
                  {org.name}
                </option>
              ))}
            </Select>
          </Field>
        )}
        {isODC ? (
          <p className="self-center text-xs text-slate-500">
            The title is the subcategory for an on-demand contractor, and is recorded from it.
          </p>
        ) : (
          <Field label="Title" required>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
        )}
        {isODC ? null : <PartnerDRIField value={driId} onChange={setDriId} />}
        <Field
          label="Contract link"
          hint="A link to the signed contract where it lives. Nothing is uploaded here."
        >
          <Input
            type="url"
            placeholder="https://…"
            value={contractLink}
            onChange={(e) => setContractLink(e.target.value)}
          />
        </Field>
        {isBeam ? (
          <BeamFields
            beamNumber={beamNumber}
            beamType={beamType}
            onBeamNumber={setBeamNumber}
            onBeamType={setBeamType}
          />
        ) : null}
        {/* No status field, for the same reason the create form has none: where a
            term sits in time is read from the dates below, and it changes over on
            the day with nothing written. */}
        <Field
          label="Start date"
          hint="Contract start, not the Slack join date. Leave blank if unknown. Leaving both dates blank means Active."
        >
          <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
        </Field>
        <Field label="Contracted end" hint="A fixed term's last day. Blank for open-ended.">
          <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </Field>
      </div>
      <div className="mt-3 flex gap-2">
        <Button
          disabled={incomplete || add.isPending}
          onClick={() =>
            add.mutate(
              {
                identityId: identity.id,
                engagement: {
                  population,
                  entityId,
                  departmentId: departmentId || undefined,
                  subcategoryId,
                  // Omitted by population, not by whether the box was filled: a
                  // title typed before switching population must not be sent for a
                  // form that has stopped showing it.
                  partnerOrganizationId: isODC ? undefined : partnerOrganizationId || undefined,
                  title: isODC ? undefined : title,
                  driId: isODC ? undefined : driId,
                  // Offered to both populations, so it is sent by whether the box was
                  // filled rather than by population — and `|| undefined` is right on
                  // a create, where an empty box and an absent field mean the same
                  // thing. On the edit form above they differ and it is sent as typed.
                  contractLink: contractLink || undefined,
                  // By subcategory, not by whether the boxes were filled, for the
                  // same reason the title is omitted by population.
                  beamDetail: isBeam
                    ? { beamNumber: beamNumber || undefined, beamType: beamType || undefined }
                    : undefined,
                  startDate: startDate || undefined,
                  endDate: endDate || undefined
                }
              },
              { onSuccess: onDone }
            )
          }
        >
          {add.isPending ? "Adding…" : "Add engagement"}
        </Button>
        <Button variant="secondary" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function AuditHistory({
  identityId,
  canReadPartners
}: {
  identityId: string;
  canReadPartners: boolean;
}) {
  const { data, isPending, error } = useIdentityAudit(identityId);
  // The log records the id a write stored; a reader came for the firm's name. The
  // same lookups the engagement form picks from are what turn one into the other,
  // and they are already loaded and cached by the time anyone opens a row.
  const { data: lookups } = useLookups();
  const { data: dris } = usePartnerDris(canReadPartners);
  const names = useMemo(() => buildAuditNames(lookups, dris?.partnerDris), [lookups, dris]);
  const events = groupAuditRows(data?.identityAuditLog ?? []);

  return (
    <Card title="History">
      <AuditEventList
        events={events}
        names={names}
        isPending={isPending}
        error={error}
        head={["When", "Who", "What"]}
        empty="No history yet."
      />
      <p className="mt-3 text-xs text-slate-500">
        One row per action. Open the details to see the exact columns and values recorded — creating
        a person writes both an identity and an engagement, and both are kept.
      </p>
    </Card>
  );
}

const OKTA_HISTORY_SHOWN = 100;

/**
 * When Okta was told about this person and what it answered: every request this
 * service sent that named them, under the history of what changed here. Shown to
 * writers only, for the reason the sync status at the top is. Closed by default, and
 * not read until it is first opened.
 */
function OktaHistory({ identityId, viewer }: { identityId: string; viewer: Viewer }) {
  const shown = viewer.oktaExportEnabled && viewer.writablePopulations.length > 0;
  const [open, setOpen] = useState(false);
  const bodyId = useId();
  const { data, isPending, error } = useIdentityOktaRequests(
    identityId,
    OKTA_HISTORY_SHOWN,
    shown && open
  );
  if (!shown) {
    return null;
  }
  const requests = data?.identityOktaRequests ?? [];

  let body: ReactNode;
  if (isPending) {
    body = <Empty>Loading…</Empty>;
  } else if (error) {
    body = (
      <p className="text-xs text-amber-800">
        Okta update history could not be read: {error.message}
      </p>
    );
  } else if (requests.length === 0) {
    body = <Empty>Nothing has been sent to Okta about them yet.</Empty>;
  } else {
    body = (
      <Table head={["When", "What", "Why", "Who", "Result"]}>
        {requests.slice(0, OKTA_HISTORY_SHOWN).map((r, i) => {
          const { what, why } = describeOktaRequest(r);
          return (
            // biome-ignore lint/suspicious/noArrayIndexKey: requests carry no id, and rows hold no state
            <tr key={`${r.sentAt}-${i}`} className="border-b border-slate-100 align-top">
              <td className="whitespace-nowrap px-2 py-2 text-slate-600">
                {new Date(r.sentAt).toLocaleString()}
              </td>
              <td className="whitespace-nowrap px-2 py-2 text-slate-900">{what}</td>
              <td className="px-2 py-2 text-slate-600">{why}</td>
              <td className="whitespace-nowrap px-2 py-2 text-slate-600">{r.actorEmail}</td>
              <td className="px-2 py-2">
                {r.succeeded ? (
                  <Badge tone="green">Accepted</Badge>
                ) : (
                  <Badge tone="red">Failed</Badge>
                )}
                {r.error ? (
                  <span className="mt-0.5 block text-xs text-red-700">{r.error}</span>
                ) : null}
                {r.oktaRequestId ? (
                  <span className="mt-0.5 block break-all font-mono text-xs text-slate-500">
                    Okta request {r.oktaRequestId}
                  </span>
                ) : null}
              </td>
            </tr>
          );
        })}
      </Table>
    );
  }

  // Card's own header holds a plain heading, and this one has to be the toggle.
  return (
    <section className="rounded-lg border border-slate-200 bg-white">
      <h2>
        <button
          type="button"
          aria-expanded={open}
          aria-controls={open ? bodyId : undefined}
          onClick={() => setOpen((v) => !v)}
          className={cn(
            "flex w-full items-center justify-between gap-3 px-4 py-2.5 text-left",
            "text-sm font-semibold text-slate-800 hover:bg-slate-50",
            open ? "rounded-t-lg" : "rounded-lg"
          )}
        >
          Okta update history
          <svg
            aria-hidden="true"
            viewBox="0 0 12 12"
            className={cn(
              "h-3 w-3 fill-none stroke-slate-500 stroke-2 transition-transform",
              open ? "rotate-180" : ""
            )}
          >
            <path d="M2 4.5 6 8.5 10 4.5" />
          </svg>
        </button>
      </h2>
      {open ? (
        <div id={bodyId} className="border-t border-slate-200 p-4">
          {body}
          <p className="mt-3 text-xs text-slate-500">
            One row per request this service sent to Okta naming them, newest first
            {requests.length > OKTA_HISTORY_SHOWN ? `, the latest ${OKTA_HISTORY_SHOWN} shown` : ""}
            . The Okta request id finds the same request in Okta&apos;s System Log.
          </p>
        </div>
      ) : null}
    </section>
  );
}
